/**
 * BookingNotesPanel.tsx
 * Panel zum Verwalten von Notizen zu einer Buchung
 * Features: Notizen hinzufügen, filtern, Erinnerungen setzen
 */

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ChatBubbleBottomCenterTextIcon,
  PlusIcon,
  BellIcon,
  UserIcon,
  ClockIcon,
  FunnelIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Booking, BookingNote } from '../../types/booking.types';
import { adminService } from '../../services/adminService';
import { format } from 'date-fns';

interface BookingNotesPanelProps {
  booking: Booking | null;
  isOpen: boolean;
  onClose: () => void;
  onAddNote: (note: Partial<BookingNote>) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
}

const BookingNotesPanel: React.FC<BookingNotesPanelProps> = ({
  booking,
  isOpen,
  onClose,
  onAddNote,
  onDeleteNote
}) => {
  // State
  const [notes, setNotes] = useState<BookingNote[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteType, setNewNoteType] = useState<'internal' | 'customer' | 'system'>('internal');
  const [isReminder, setIsReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when dialog opens/closes - MUST be before early return
  useEffect(() => {
    if (isOpen && booking) {
      loadNotes();
      resetForm();
    }
  }, [isOpen, booking]);

  if (!isOpen || !booking) return null;

  // Load notes from API - Backend returns BookingNoteDto[]
  const loadNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminService.getBookingNotes(parseInt(booking.id));
      // Transform Backend BookingNoteDto to Frontend BookingNote
      const transformedNotes: BookingNote[] = (Array.isArray(response) ? response : []).map((dto: any) => ({
        id: dto.id.toString(),
        bookingId: dto.bookingId.toString(),
        text: dto.text,
        type: dto.type,
        createdBy: dto.createdBy,
        createdAt: dto.createdAt,
        isReminder: dto.isReminder,
        reminderDate: dto.reminderDate
      }));
      setNotes(transformedNotes);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Fehler beim Laden der Notizen');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewNoteText('');
    setNewNoteType('internal');
    setIsReminder(false);
    setReminderDate('');
    setError(null);
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      setError('Notiz darf nicht leer sein');
      return;
    }

    if (isReminder && !reminderDate) {
      setError('Bitte wählen Sie ein Erinnerungsdatum');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newNote: Partial<BookingNote> = {
        bookingId: booking.id,
        text: newNoteText.trim(),
        type: newNoteType,
        isReminder,
        reminderDate: isReminder ? reminderDate : undefined,
        createdBy: 'Current Admin', // TODO: Get from auth context
        createdAt: new Date().toISOString()
      };

      await onAddNote(newNote);

      // Reload notes from server instead of optimistic update
      await loadNotes();

      resetForm();
    } catch (err: any) {
      console.error('Error adding note:', err);
      setError(err.message || 'Fehler beim Hinzufügen der Notiz');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete note - Backend expects number
  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Möchten Sie diese Notiz wirklich löschen?')) {
      return;
    }

    try {
      // Call Backend DELETE /api/admin/bookings/{bookingId}/notes/{noteId}
      await adminService.deleteBookingNote(parseInt(booking.id), parseInt(noteId));

      // Reload notes to ensure consistency
      await loadNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Fehler beim Löschen der Notiz');
    }
  };

  // Filter notes
  const filteredNotes = filterType === 'all'
    ? notes
    : notes.filter(note => note.type === filterType);

  // Get note type badge color
  const getNoteTypeBadge = (type: string) => {
    switch (type) {
      case 'internal':
        return { label: 'Intern', color: '#4a9eff' };
      case 'customer':
        return { label: 'Kunde', color: '#4ade80' };
      case 'system':
        return { label: 'System', color: '#999' };
      default:
        return { label: type, color: '#999' };
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content booking-notes-panel"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '700px', maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ChatBubbleBottomCenterTextIcon className="w-6 h-6" style={{ color: '#4a9eff' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Notizen</h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#999' }}>
                {booking.bookingNumber} - {booking.customerName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="btn-close" disabled={loading}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ padding: '20px' }}>
          {/* Error Display */}
          {error && (
            <div className="alert alert-error" style={{
              background: 'rgba(255, 74, 74, 0.1)',
              border: '1px solid rgba(255, 74, 74, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '15px',
              color: '#ff4a4a'
            }}>
              {error}
            </div>
          )}

          {/* Add Note Form */}
          <div className="add-note-form" style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              color: '#ffffff',
              fontSize: '14px'
            }}>
              Neue Notiz hinzufügen
            </label>

            <textarea
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Notiz eingeben..."
              rows={4}
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '12px'
              }}
              disabled={loading}
            />

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '12px'
            }}>
              {/* Note Type */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontSize: '12px',
                  color: '#999'
                }}>
                  Notiz-Typ
                </label>
                <select
                  value={newNoteType}
                  onChange={(e) => setNewNoteType(e.target.value as any)}
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    padding: '8px',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  disabled={loading}
                >
                  <option value="internal">Intern (nur Admins)</option>
                  <option value="customer">Kunde (sichtbar für Kunde)</option>
                  <option value="system">System</option>
                </select>
              </div>

              {/* Reminder Date */}
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginBottom: '5px',
                  fontSize: '12px',
                  color: '#999',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={isReminder}
                    onChange={(e) => setIsReminder(e.target.checked)}
                    disabled={loading}
                    style={{ width: '14px', height: '14px' }}
                  />
                  <BellIcon className="w-4 h-4" />
                  Erinnerung
                </label>
                {isReminder && (
                  <input
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      padding: '8px',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    disabled={loading}
                  />
                )}
              </div>
            </div>

            <button
              onClick={handleAddNote}
              disabled={loading || !newNoteText.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: loading || !newNoteText.trim()
                  ? 'rgba(102, 126, 234, 0.3)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: loading || !newNoteText.trim() ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              <PlusIcon className="w-5 h-5" />
              Notiz hinzufügen
            </button>
          </div>

          {/* Filter */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '15px'
          }}>
            <FunnelIcon className="w-5 h-5" style={{ color: '#999' }} />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">Alle Notizen ({notes.length})</option>
              <option value="internal">Intern ({notes.filter(n => n.type === 'internal').length})</option>
              <option value="customer">Kunde ({notes.filter(n => n.type === 'customer').length})</option>
              <option value="system">System ({notes.filter(n => n.type === 'system').length})</option>
            </select>
          </div>

          {/* Notes List */}
          <div className="notes-list" style={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {loading && notes.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#999'
              }}>
                Lade Notizen...
              </div>
            ) : filteredNotes.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#999'
              }}>
                <ChatBubbleBottomCenterTextIcon className="w-12 h-12" style={{ margin: '0 auto 10px', opacity: 0.3 }} />
                <p>Keine Notizen vorhanden</p>
              </div>
            ) : (
              filteredNotes.map((note) => {
                const badge = getNoteTypeBadge(note.type);
                return (
                  <div
                    key={note.id}
                    className="note-item"
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '12px',
                      position: 'relative'
                    }}
                  >
                    {/* Note Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: '10px'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          background: `${badge.color}20`,
                          color: badge.color,
                          border: `1px solid ${badge.color}40`,
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>
                          {badge.label}
                        </span>
                        {note.isReminder && (
                          <span style={{
                            background: 'rgba(255, 215, 0, 0.1)',
                            color: '#ffd700',
                            border: '1px solid rgba(255, 215, 0, 0.3)',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <BellIcon className="w-3 h-3" />
                            {note.reminderDate && format(new Date(note.reminderDate), 'dd.MM.yyyy')}
                          </span>
                        )}
                      </div>
                      {onDeleteNote && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                          title="Notiz löschen"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Note Text */}
                    <p style={{
                      margin: '0 0 10px 0',
                      color: '#e0e0e0',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {note.text}
                    </p>

                    {/* Note Footer */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      fontSize: '12px',
                      color: '#999'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <UserIcon className="w-4 h-4" />
                        {note.createdBy}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ClockIcon className="w-4 h-4" />
                        {format(new Date(note.createdAt), 'dd.MM.yyyy HH:mm')}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '15px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingNotesPanel;
